import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Slider } from '../ui/slider';

const incluido = [
  'Empleados ilimitados fichando en tablet, ordenador y móvil',
  'Acceso con huella o Face ID en el móvil del empleado',
  'Control GPS y gestión de incidencias de ubicación',
  'Suite completa de 6 informes profesionales (PDF y Excel)',
  'Envío automático de informes a gestoría/RRHH por email',
  'Autocierre inteligente con validación del empleado',
  'Cuadrantes de turnos con rotaciones en un clic',
  'Horas extra, nocturnas y jornada esperada por día',
  'Calendario laboral, festivos y ausencias',
  'Roles y permisos (gestoría, auditor, colaborador)',
  'Validación legal de cambios y auditoría completa',
  'Varios centros de trabajo y departamentos',
  'Soporte por teléfono y email en español',
];

const fmt = (n) => n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const Pricing = ({ onDemo }) => {
  const [anual, setAnual] = useState(true);
  const [empleados, setEmpleados] = useState([10]);
  const n = empleados[0];
  const total = anual ? n * 15 : n * 1.5;

  return (
    <section id="precios" className="py-20 sm:py-28 bg-bone" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-2xl mb-14">
          <p className="font-body-landing text-terracotta font-bold text-sm tracking-[0.15em] uppercase mb-4">Precios</p>
          <h2 className="font-display font-black text-forest-900 text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight mb-4">
            Un precio simple. Sin sorpresas.
          </h2>
          <p className="font-body-landing text-forest-700 text-base md:text-lg">
            Pagas solo por empleado activo. Todas las funcionalidades incluidas, siempre.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
          className="grid lg:grid-cols-5 rounded-3xl border border-forest-900/10 bg-white shadow-[0_24px_60px_rgb(15,28,20,0.08)] overflow-hidden"
        >
          <div className="lg:col-span-3 p-8 sm:p-12">
            <div className="flex items-center gap-4 mb-10">
              <span className={`font-body-landing font-bold text-sm ${!anual ? 'text-forest-900' : 'text-forest-700/50'}`}>Mensual</span>
              <Switch checked={anual} onCheckedChange={setAnual} data-testid="pricing-toggle" className="data-[state=checked]:bg-terracotta" />
              <span className={`font-body-landing font-bold text-sm ${anual ? 'text-forest-900' : 'text-forest-700/50'}`}>Anual</span>
              <span className="rounded-full bg-forest-100 text-forest-700 font-body-landing text-xs font-bold px-3 py-1">Ahorra 2 meses</span>
            </div>

            <div className="mb-10">
              <div className="flex justify-between items-end mb-4">
                <p className="font-body-landing text-forest-700 text-sm font-semibold">¿Cuántos empleados tienes?</p>
                <p className="font-display font-black text-forest-900 text-2xl" data-testid="pricing-employees">{n}</p>
              </div>
              <Slider value={empleados} onValueChange={setEmpleados} min={1} max={100} step={1} data-testid="pricing-slider" className="[&_[role=slider]]:bg-terracotta [&_[role=slider]]:border-terracotta" />
              <div className="flex justify-between font-body-landing text-xs text-forest-700/50 mt-2"><span>1</span><span>100</span></div>
            </div>

            <div className="flex items-baseline gap-2 mb-2">
              <span className="font-display font-black text-forest-900 text-5xl sm:text-6xl" data-testid="pricing-total">{fmt(total)} €</span>
              <span className="font-body-landing text-forest-700 text-lg">{anual ? '/año' : '/mes'}</span>
            </div>
            <p className="font-body-landing text-forest-700 text-sm mb-8" data-testid="pricing-detail">
              {anual
                ? <>15 € por empleado al año · equivale a <strong>1,25 €/empleado al mes</strong></>
                : <>1,50 € por empleado al mes</>}
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild size="lg" className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white font-body-landing font-bold h-12 px-8">
                <Link to="/registro" data-testid="pricing-trial-btn">Prueba gratis 15 días</Link>
              </Button>
              <Button size="lg" variant="outline" onClick={onDemo} className="rounded-full border-forest-900/20 text-forest-900 hover:bg-forest-100 font-body-landing font-bold h-12 px-8 bg-transparent" data-testid="pricing-demo-btn">
                Ver demo
              </Button>
            </div>
          </div>

          <div className="lg:col-span-2 bg-forest-900 noise-overlay p-8 sm:p-12">
            <p className="font-display font-bold text-bone text-lg mb-6">Todo incluido, siempre:</p>
            <ul className="space-y-3.5">
              {incluido.map((f) => (
                <li key={f} className="flex gap-3 font-body-landing text-sm text-bone/80">
                  <Check className="w-4 h-4 text-terracotta shrink-0 mt-0.5" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
